import {
  AnyAction,
  createSlice,
  PayloadAction,
  Reducer,
  Slice,
} from '@reduxjs/toolkit';

import { PaginationDefaults } from '../../../../shared/utils/pagination-defaults.util';
import { FiltersPayloadModel } from '../../../shared/models/filters-payload.model';
import { AppsFilters } from '../../models/apps-filters.model';
import { AppsHeaderAndPaginationFilters } from '../../models/apps-header-and-pagination-filters.model';
import { ApplicationsState } from '../../models/slices/applications-state.model';
import { AppsSliceReducers } from '../../models/slices/apps-slice-reducers.model';
import { getAppsTotalCount } from '../../thunks/get-apps-total-count.thunk';
import { getApps } from '../../thunks/get-apps.thunk';

const filtersInitialState: AppsHeaderAndPaginationFilters = {
  header: {
    nameOrDescription: undefined,
    categories: undefined,
    platforms: undefined,
    status: undefined,
    isFeatured: undefined,
  },
  pagination: {
    totalCount: PaginationDefaults.totalCount,
    pageNumber: PaginationDefaults.pageNumber,
    size: PaginationDefaults.size,
    offset: PaginationDefaults.offset,
  },
};

const initialState: ApplicationsState = {
  apps: null,
  filters: filtersInitialState,
  areAppsFiltersVisible: false,
};

export const appsSlice: Slice<ApplicationsState, AppsSliceReducers, string> =
  createSlice({
    name: 'apps',
    initialState: initialState,
    reducers: {
      clearFilters(state) {
        state.filters = filtersInitialState;
      },

      clearAppsState(state) {
        Object.assign(state, initialState);
      },

      filterAppsByNameOrDescription(
        state,
        { payload }: PayloadAction<FiltersPayloadModel<AppsFilters>>
      ) {
        state.filters.header = {
          ...state.filters.header,
          nameOrDescription: payload.filterValue,
        };
      },

      filterAppsByCategories(state, { payload }: PayloadAction<number[]>) {
        state.filters.header = {
          ...state.filters.header,
          categories: payload,
        };
      },

      filterAppsByPlatforms(state, { payload }: PayloadAction<number[]>) {
        state.filters.header = {
          ...state.filters.header,
          platforms: payload,
        };
      },

      filterAppsByIsFeatured(
        state,
        { payload }: PayloadAction<boolean | undefined>
      ) {
        state.filters.header = {
          ...state.filters.header,
          isFeatured: payload,
        };
      },

      filterAppsByStatus(state, { payload }: PayloadAction<string>) {
        state.filters.header = {
          ...state.filters.header,
          status: payload,
        };
      },

      addPagination(state, action) {
        state.filters.pagination.size = action.payload.size;
        state.filters.pagination.offset = action.payload.offset;
        state.filters.pagination.pageNumber = action.payload.pageNumber;
      },

      resetPagination(state, action) {
        state.filters.pagination.offset = PaginationDefaults.offset;
        state.filters.pagination.size = action.payload.size;
        state.filters.pagination.pageNumber = PaginationDefaults.pageNumber;
      },

      appsToggleFiltersVisibility(state) {
        state.areAppsFiltersVisible = !state.areAppsFiltersVisible;
      },
    },

    extraReducers: builder => {
      builder
        .addCase(getApps.fulfilled, (state: ApplicationsState, action) => {
          state.apps = action.payload;
        })
        .addCase(getAppsTotalCount.fulfilled, function (state, action) {
          state.filters.pagination.totalCount = action.payload;
        });
    },
  });

const appsSliceReducers: Reducer<ApplicationsState, AnyAction> =
  appsSlice.reducer;

export const {
  clearFilters,
  filterAppsByCategories,
  filterAppsByPlatforms,
  filterAppsByIsFeatured,
  filterAppsByNameOrDescription,
  filterAppsByStatus,
  addPagination,
  resetPagination,
  clearAppsState,
  appsToggleFiltersVisibility,
} = appsSlice.actions;

export default appsSliceReducers;
